import { datadogLogs } from '@datadog/browser-logs';

import { config } from '../config';
import {
  BARE_NETWORK_ERROR_MESSAGE_PATTERNS,
  CEFSHARP_BOT_ERROR_PATTERN,
  MODULE_IMPORT_ABORT_MESSAGE_PATTERNS,
  THIRD_PARTY_NOISE_MESSAGE_PATTERNS,
  THIRD_PARTY_NOISE_PATTERNS,
} from './thirdPartyNoisePatterns';

type SeverityLevel = 'debug' | 'info' | 'warn' | 'error';

export class Logger {
  private static datadogInitialized = false;

  static init() {
    if (config.dataDogEnabled && !Logger.datadogInitialized) {
      datadogLogs.init({
        clientToken: config.dataDog.clientToken,
        site: config.dataDog.site,
        service: config.dataDog.service,
        env: config.environment,
        version: config.version,
        sessionSampleRate: 100,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: ['error'],
        beforeSend: (event) => {
          // A fetch/XHR aborted mid-request (e.g. a component unmounting before it resolves)
          // is reported with status 0 — there's no real HTTP response to attribute, so don't send it.
          if (event.http?.status_code === 0) {
            return false;
          }
          // A dynamically-imported JS chunk whose load gets aborted mid-flight by page navigation
          // is the module loader's version of the aborted-fetch case just above.
          if (MODULE_IMPORT_ABORT_MESSAGE_PATTERNS.some((pattern) => event.message.includes(pattern))) {
            return false;
          }
          if (THIRD_PARTY_NOISE_PATTERNS.some((pattern) => event.message.includes(pattern) || event.error?.stack?.includes(pattern))) {
            return false;
          }
          if (THIRD_PARTY_NOISE_MESSAGE_PATTERNS.some((pattern) => event.message.includes(pattern))) {
            return false;
          }
          // Datadog's forwarded-logs path doesn't consistently put the message in event.message vs event.error.message.
          const errorMessage = event.message || event.error?.message;
          // Bare cross-origin script errors are unactionable noise; browsers sometimes wrap the placeholder (e.g. `Uncaught "Script error."`), so match substring not exact equality.
          // A first-party error always carries a real stack (we control the bundle/sourcemaps) — an
          // entirely stack-less error is the browser's security redaction for a cross-origin script, no
          // matter what its message text says, so match on the placeholder stack rather than the message.
          if (
            (typeof errorMessage === 'string' &&
              (errorMessage.includes('Script error.') ||
                (errorMessage.includes('undefined') && /at undefined @\s*$/.test(event.error?.stack ?? '')))) ||
            event.error?.stack === 'No stack, consider using an instance of Error'
          ) {
            return false;
          }
          if (typeof errorMessage === 'string' && CEFSHARP_BOT_ERROR_PATTERN.test(errorMessage)) {
            return false;
          }
          if (typeof errorMessage === 'string' && BARE_NETWORK_ERROR_MESSAGE_PATTERNS.some((pattern) => errorMessage.includes(pattern))) {
            return false;
          }
          return true;
        },
      });
      Logger.datadogInitialized = true;
    }
  }

  static setUser(user: { id?: string; email?: string }) {
    if (Logger.datadogInitialized) {
      datadogLogs.setUser(user);
    }
  }

  static debug(message: string, context?: object) {
    Logger.log('debug', message, context);
  }

  static info(message: string, context?: object) {
    Logger.log('info', message, context);
  }

  static warn(message: string, context?: object, error?: unknown) {
    Logger.log('warn', message, context, error);
  }

  static error(message: string, context?: object, error?: unknown) {
    Logger.log('error', message, context, error);
  }

  private static log(level: SeverityLevel, message: string, context?: object, error?: unknown) {
    if (config.consoleLogsEnabled) {
      const logFn = console[level] || console.log;
      const consoleArgs = [`[${level.toUpperCase()}]`, message, ...(context ? [context] : []), ...(error !== undefined ? [error] : [])];
      logFn(...consoleArgs);
    }

    if (Logger.datadogInitialized) {
      // Datadog only computes a stack trace (and surfaces the log in Error Tracking) when
      // an Error instance is passed as the 3rd arg, so non-Error values must be wrapped.
      const normalizedError = error === undefined ? undefined : error instanceof Error ? error : new Error(String(error));
      datadogLogs.logger[level](message, context, normalizedError);
    }
  }
}
